import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Get environment variables from window (injected by HTML)
declare global {
  interface Window {
    SUPABASE_URL: string;
    SUPABASE_ANON_KEY: string;
  }
}

let supabaseClient: SupabaseClient | null = null;

/**
 * Get or create Supabase client instance (singleton)
 */
export function getSupabase(): SupabaseClient {
  if (!supabaseClient) {
    const url = window.SUPABASE_URL;
    const key = window.SUPABASE_ANON_KEY;

    if (!url || !key) {
      throw new Error('Supabase configuration missing. Check environment variables.');
    }

    supabaseClient = createClient(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    });
  }

  return supabaseClient;
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser() {
  const supabase = getSupabase();
  const { data: { user }, error } = await supabase.auth.getUser();

  if (error || !user) {
    return null;
  }

  return user;
}

/**
 * Get user's membership tier and status
 */
export async function getUserMembership() {
  const user = await getCurrentUser();
  if (!user) return null;

  const supabase = getSupabase();
  const { data: membership, error } = await supabase
    .from('memberships')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error || !membership) {
    return null;
  }

  return membership;
}

/**
 * Get user's profile
 */
export async function getUserProfile() {
  const user = await getCurrentUser();
  if (!user) return null;

  const supabase = getSupabase();
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error || !profile) {
    return null;
  }

  return profile;
}

/**
 * Get full user data (user, membership, profile)
 */
export async function getUserData() {
  const user = await getCurrentUser();
  if (!user) return null;

  const [membership, profile] = await Promise.all([
    getUserMembership(),
    getUserProfile(),
  ]);

  return {
    user,
    membership,
    profile,
  };
}

/**
 * Check if user has access to a specific tier
 */
export async function hasTierAccess(requiredTier: 'FRIENDS' | 'COMPANION' | 'MENTOR'): Promise<boolean> {
  const membership = await getUserMembership();
  if (!membership || membership.status !== 'active') return false;

  const tierHierarchy: Record<string, number> = {
    'FRIENDS': 1,
    'COMPANION': 2,
    'MENTOR': 3,
  };

  const userTierLevel = tierHierarchy[membership.tier] || 0;
  const requiredTierLevel = tierHierarchy[requiredTier] || 0;

  return userTierLevel >= requiredTierLevel;
}

/**
 * Get user's companion name from journey data
 */
export async function getCompanionName(): Promise<string> {
  const user = await getCurrentUser();
  if (!user) return 'Emi'; // Default fallback

  const supabase = getSupabase();
  const { data: journey, error } = await supabase
    .from('user_journey')
    .select('companion_name')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error || !journey || !journey.companion_name) {
    return 'Emi'; // Default fallback
  }

  return journey.companion_name;
}

/**
 * Sign out the current user
 */
export async function signOut() {
  const supabase = getSupabase();
  await supabase.auth.signOut();
  window.location.href = '/';
}
